import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Ably from 'ably';
import { Loader } from './loader';
import { HandoverSignatureModal } from './handoverSignature';
import AnimatedBottomSheet from './animatedBottomSheet';

import { useCustomDispatch, useCustomNavigate } from '../../hooks';
import { fetchPharmacyDetails } from '../../services/apis/dispense/getPharmacyDetail';
import { authSelectedPharmacist } from '../../services/apis/auth/selectedPharmacistID';

import * as Icons from '../../assets/svg/icons';
import { apiKey } from '../../networking/urlEndPoints';
import * as routeNames from '../../routes/routeName'
import Cookies from 'js-cookie';
import { getCookieOptions } from '../../utils/helper/security';
import { fetchDriverRouteOptions, recordDriverActivitySignal, selectDriverRoutes } from '../../services/apis/dispense/routeAudit';
import {
    getActivePharmacistOptions,
    getRouteStatusLabel,
    getSelectedRoutesFromOptions,
    getVisibleSelectedPharmacistName,
    sortRouteOptions,
} from '../../utils/helper/routeAuditUi';
import { normalizeBackendAssetUrl } from '../../utils/helper/url';
import fallbackAvatar from '../../assets/images/userimg.png';

const routeRefreshAuditEventTypes = new Set([
    'route_timeout_expired',
    'route_ownership_released',
]);

const resolveRouteSelectionErrorMessage = (error, fallback) =>
    error?.response?.data?.data?.error?.original_delivery_personnel_signature?.[0]
    || error?.response?.data?.data?.error?.routes?.[0]
    || error?.response?.data?.message
    || fallback;

let sharedPharmacistsListRealtime = null;

const getSharedPharmacistsListRealtime = () => {
    if (!apiKey) {
        return null;
    }

    const state = `${sharedPharmacistsListRealtime?.connection?.state ?? ''}`.toLowerCase();
    if (!sharedPharmacistsListRealtime || state === 'closed' || state === 'failed') {
        sharedPharmacistsListRealtime = new Ably.Realtime({ key: apiKey });
    }

    return sharedPharmacistsListRealtime;
};

const RouteCardsSkeleton = () => (
    <div className="grid grid-cols-2 gap-2">
        {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="rounded-md border border-gray-200 bg-white px-3 py-3 animate-pulse">
                <div className="flex items-start gap-2">
                    <div className="mt-1 h-4 w-4 rounded-sm bg-gray-200" />
                    <div className="min-w-0 flex-1">
                        <div className="h-4 w-12 rounded bg-gray-200" />
                        <div className="mt-2 h-3 w-24 rounded bg-gray-100" />
                        <div className="mt-2 h-3 w-20 rounded bg-gray-100" />
                    </div>
                </div>
            </div>
        ))}
    </div>
);

const PharmacistCardsSkeleton = () => (
    <div className="grid grid-cols-5 gap-2">
        {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="flex min-w-0 flex-col items-center gap-2">
                <div className="h-16 w-16 shrink-0 rounded-lg bg-gray-200 animate-pulse" />
                <div className="h-3 w-14 rounded bg-gray-100 animate-pulse" />
            </div>
        ))}
    </div>
);

const PharmacistNameMarquee = ({ text, isSelected }) => {
    const containerRef = React.useRef(null);
    const textRef = React.useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
        const updateOverflowState = () => {
            if (!containerRef.current || !textRef.current) {
                return;
            }

            setIsOverflowing(textRef.current.scrollWidth > containerRef.current.clientWidth);
        };

        updateOverflowState();
        window.addEventListener('resize', updateOverflowState);
        return () => window.removeEventListener('resize', updateOverflowState);
    }, [text]);

    const textClassName = `text-center text-xs font-medium whitespace-nowrap ${isSelected ? 'text-blue-600' : 'text-slate-700'}`;

    if (!isOverflowing) {
        return (
            <div ref={containerRef} className="mx-auto flex w-full max-w-[5rem] items-center justify-center overflow-hidden">
                <p ref={textRef} className={textClassName} title={text}>
                    {text}
                </p>
            </div>
        );
    }

    return (
        <div ref={containerRef} className="mx-auto w-full max-w-[5rem] overflow-hidden">
            <div className="dashboard-marquee-track" style={{ animationDuration: '8s' }}>
                <p ref={textRef} className={`${textClassName} shrink-0 pr-8`} title={text}>
                    {text}
                </p>
                <p className={`${textClassName} shrink-0 pr-8`} aria-hidden="true">
                    {text}
                </p>
            </div>
        </div>
    );
};

const formatPharmacistDisplayName = (value) =>
    `${value ?? ''}`
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');

export const PharmacistsList = ({ showPharmacistModal,
    setShowPharmacistModal,
    allPharmacistListResponse,
    pharmacyId,
    driverSelectedPharmacy,
    forcedPharmacistId = null,
    hidePharmacistSelection = false,
    flexiUser,
    hcpUser
}) => {

    const pharmacistList = allPharmacistListResponse?.data?.data
    const activePharmacistOptions = useMemo(
        () => getActivePharmacistOptions(pharmacistList),
        [pharmacistList]
    )

    const [selectedPharmacist, setSelectedPharmacist] = useState(null)
    const [routeOptions, setRouteOptions] = useState([])
    const [selectedRoutes, setSelectedRoutes] = useState([])
    const [routesLoading, setRoutesLoading] = useState(false)
    const [routeError, setRouteError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showRouteSignatureModal, setShowRouteSignatureModal] = useState(false)
    const dispatch = useCustomDispatch()
    const navigate = useCustomNavigate()
    const selectedRouteCount = selectedRoutes.length
    const isFlexiUser = flexiUser === true || flexiUser === 'true' || flexiUser === 1

    useEffect(() => {
        if (!showPharmacistModal) {
            return
        }

        if (hidePharmacistSelection && forcedPharmacistId) {
            setSelectedPharmacist(forcedPharmacistId)
            return
        }

        if (!hidePharmacistSelection) {
            setSelectedPharmacist(null)
        }
    }, [forcedPharmacistId, hidePharmacistSelection, showPharmacistModal])

    useEffect(() => {
        if (showPharmacistModal) {
            return
        }

        setShowRouteSignatureModal(false)
    }, [showPharmacistModal])

    const loadRouteOptions = useCallback(async () => {
        if (!pharmacyId) {
            return
        }

        setRoutesLoading(true)
        setRouteError('')
        try {
            const routes = await fetchDriverRouteOptions(pharmacyId)
            const nextOptions = sortRouteOptions(routes || [])
            setRouteOptions(nextOptions)
            setSelectedRoutes(getSelectedRoutesFromOptions(nextOptions))
        } catch (_) {
            setRouteError('Unable to load routes for this pharmacy.')
        } finally {
            setRoutesLoading(false)
        }
    }, [pharmacyId])

    const handlePharmacistChange = (e) => {
        setSelectedPharmacist(e.target.value)
    }

    const handleRouteToggle = (routeOption) => {
        if (routeOption?.is_locked || routeOption?.is_selected) {
            return
        }

        const route = routeOption?.route
        if (!route) {
            return
        }

        setSelectedRoutes((currentRoutes) =>
            currentRoutes.includes(route)
                ? currentRoutes.filter((value) => value !== route)
                : [...currentRoutes, route]
        )
    }

    useEffect(() => {
        if (!showPharmacistModal || !pharmacyId) {
            return;
        }

        loadRouteOptions()
    }, [showPharmacistModal, pharmacyId, loadRouteOptions])

    useEffect(() => {
        if (!showPharmacistModal || !pharmacyId || !apiKey) {
            return undefined
        }

        const ably = getSharedPharmacistsListRealtime()
        if (!ably) {
            return undefined
        }
        const channel = ably.channels.get('pharmacy-notifications')
        const eventName = `pharmacy-${pharmacyId}`
        const handler = (messageEvent) => {
            const eventData = messageEvent?.data
            if (
                eventData?.type === 'audit_trail_updated'
                && routeRefreshAuditEventTypes.has(eventData?.event_type)
            ) {
                loadRouteOptions()
            }
        }

        channel.subscribe(eventName, handler)
        return () => {
            channel.unsubscribe(eventName, handler)
        }
    }, [showPharmacistModal, pharmacyId, loadRouteOptions])

    const handleSavePharmacist = () => {
        if (!selectedRoutes.length) {
            setRouteError('Select at least one route before continuing.')
            return
        }

        if (!hidePharmacistSelection && !selectedPharmacist) {
            setRouteError('Select a pharmacist before continuing.')
            return
        }

        setRouteError('')
        setShowRouteSignatureModal(true)
    }

    const handleConfirmDeliverySetupSignature = async (originalDeliveryPersonnelSignature) => {
        if (!originalDeliveryPersonnelSignature) {
            setRouteError('Signature is required.')
            return
        }

        setIsSubmitting(true)
        setRouteError('')

        try {
            // Single API call — backend now persists the pharmacist-driver link
            // inside select-routes when pharmacist_id is provided, so no separate
            // save-pharmacist-for-driver call is needed.
            const routeSelection = await selectDriverRoutes({
                pharmacyId,
                routes: selectedRoutes,
                pharmacistId: selectedPharmacist,
                originalDeliveryPersonnelSignature,
            })

            if (routeSelection?.route_options?.length) {
                const nextOptions = sortRouteOptions(routeSelection.route_options)
                setRouteOptions(nextOptions)
                setSelectedRoutes(getSelectedRoutesFromOptions(nextOptions))
            }

            if (routeSelection?.conflicts?.length) {
                setRouteError(`${routeSelection.conflicts.map((item) => item.route).join(', ')} already picked by another user.`)
                setShowRouteSignatureModal(false)
                setIsSubmitting(false)
                return
            }

            // Convert to number if it's a string (from select dropdown)
            const pharmacistIdToStore = typeof selectedPharmacist === 'string'
                ? parseInt(selectedPharmacist, 10)
                : selectedPharmacist;

            // Store selected pharmacist ID in Redux for video calls
            dispatch(authSelectedPharmacist(pharmacistIdToStore));
            recordDriverActivitySignal({
                pharmacyId,
                route: selectedRoutes.length === 1 ? selectedRoutes[0] : undefined,
            });

            setShowRouteSignatureModal(false)
            setShowPharmacistModal(false)
            setSelectedPharmacist(hidePharmacistSelection && forcedPharmacistId ? forcedPharmacistId : null)
            setSelectedRoutes([])
            setIsSubmitting(false)
            Cookies.set('verified', 1, getCookieOptions({ expiresHours: 11 }))

            if (hcpUser === 0 && isFlexiUser) navigate(routeNames.DASHBOARD_PHARMACY);
            else if (hcpUser === 0) navigate(routeNames.ADD_VITALS);
            else if (isFlexiUser) navigate(routeNames.flexi_delivery);
            else navigate(routeNames.DASHBOARD_PHARMACY);

            // Fetch pharmacy details in the background after navigation so it doesn't delay the route transition
            dispatch(fetchPharmacyDetails(pharmacyId));

        } catch (error) {
            setShowRouteSignatureModal(false)
            setRouteError(resolveRouteSelectionErrorMessage(error, 'Unable to save the delivery setup.'))
            setIsSubmitting(false)
        }
    }

    const handleSkipToDashboard = () => {
        setRouteError('')
        setShowRouteSignatureModal(false)
        setShowPharmacistModal(false)
        setSelectedRoutes([])
        Cookies.set('verified', 1, getCookieOptions({ expiresHours: 11 }))
        navigate(routeNames.DASHBOARD_PHARMACY)
    }

    return (
        <React.Fragment>
            <AnimatedBottomSheet
                isOpen={showPharmacistModal}
                onClose={() => setShowPharmacistModal(false)}
                fullScreen
                ariaLabel="Select Pharmacist and Routes"
            >
                        <div className="flex h-full min-h-0 flex-col bg-white">
                            <div className="flex items-center justify-between p-2 px-4 md:p-3 border-b border-dashed">
                                <h3 className="text-sm font-bold text-gray-900">
                                    {driverSelectedPharmacy?.pharmacies?.name?.toUpperCase()}
                                </h3>

                                <button type="button"
                                    onClick={() => setShowPharmacistModal(false)}
                                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8
                                                ms-auto inline-flex justify-center items-center"
                                    data-modal-hide="bottom-right-modal">
                                    <Icons.CloseModelIcon />
                                    <span className="sr-only">Close modal</span>
                                </button>
                            </div>

                            <div className="otp-route-selection-scroller min-h-0 flex-1 overflow-y-auto bg-slate-50/70 px-2 py-2">
                                <div className="mx-auto flex w-full max-w-lg flex-col gap-3 px-2 py-3">
                                            {!hidePharmacistSelection ? (
                                                <div>
                                                    <p className="text-sm font-semibold text-slate-900 mb-3">Select Pharmacist</p>
                                                    {allPharmacistListResponse?.loading ? (
                                                        <PharmacistCardsSkeleton />
                                                    ) : activePharmacistOptions.length === 0 ? (
                                                        <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center">
                                                            <p className="text-sm font-medium text-slate-600">No pharmacists available.</p>
                                                        </div>
                                                    ) : (
                                                        <div className="grid grid-cols-5 gap-2">
                                                            {activePharmacistOptions.map((pharmacist) => {
                                                                const isSelected = selectedPharmacist === pharmacist.id
                                                                const displayName = formatPharmacistDisplayName(pharmacist.name)
                                                                const initials = displayName
                                                                    .split(' ')
                                                                    .map((n) => n[0])
                                                                    .join('')
                                                                    .toUpperCase()
                                                                    .slice(0, 2)
                                                                const profileImageUrl = normalizeBackendAssetUrl(
                                                                    pharmacist?.profile_image
                                                                    || pharmacist?.profileImage
                                                                    || pharmacist?.photo
                                                                )
                                                                const isDefaultProfileImage = typeof profileImageUrl === 'string'
                                                                    && /\/user\.png(\?|#|$)/i.test(profileImageUrl)
                                                                const colors = [
                                                                    'bg-blue-500',
                                                                    'bg-purple-500',
                                                                    'bg-pink-500',
                                                                    'bg-green-500',
                                                                    'bg-orange-500',
                                                                    'bg-red-500',
                                                                    'bg-indigo-500',
                                                                    'bg-cyan-500',
                                                                ]
                                                                const colorIndex = pharmacist.id % colors.length
                                                                const bgColor = colors[colorIndex]

                                                                return (
                                                                    <button
                                                                        key={pharmacist.id}
                                                                        type="button"
                                                                        onClick={() => handlePharmacistChange({ target: { value: pharmacist.id } })}
                                                                        className={`relative flex min-w-0 flex-col items-center gap-2 transition ${
                                                                            isSelected ? 'opacity-100' : 'opacity-90 hover:opacity-100'
                                                                        }`}
                                                                    >
                                                                        <div className={`relative shrink-0 rounded-lg overflow-hidden transition ${
                                                                            isSelected ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                                                                        }`}>
                                                                            {profileImageUrl && !isDefaultProfileImage ? (
                                                                                <img
                                                                                    src={profileImageUrl}
                                                                                    alt={displayName}
                                                                                    className="h-16 w-16 object-cover"
                                                                                    onError={(event) => {
                                                                                        event.currentTarget.src = fallbackAvatar
                                                                                    }}
                                                                                />
                                                                            ) : (
                                                                                <div
                                                                                    className={`flex h-16 w-16 items-center justify-center rounded-lg ${bgColor} text-lg font-bold text-white`}
                                                                                >
                                                                                    {initials}
                                                                                </div>
                                                                            )}
                                                                            {isSelected && (
                                                                                <div className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 shadow-md">
                                                                                    <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                                    </svg>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <PharmacistNameMarquee
                                                                            text={displayName}
                                                                            isSelected={isSelected}
                                                                        />
                                                                    </button>
                                                                )
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : null}

                                            <div className="pt-1">
                                                <div className="mb-3 flex items-center justify-between gap-3">
                                                    <p className="text-sm font-semibold text-slate-900">Routes</p>
                                                    {/* {selectedRouteCount > 0 ? (
                                                        <button
                                                            type="button"
                                                            onClick={handleClearSelectedRoutes}
                                                            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
                                                        >
                                                            Clear
                                                        </button>
                                                    ) : null} */}
                                                </div>
                                                {routesLoading ? (
                                                    <RouteCardsSkeleton />
                                                ) : routeOptions.length > 0 ? (
                                                    <div className="">
                                                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                                                            {routeOptions.map((routeOption) => {
                                                                const isChecked = selectedRoutes.includes(routeOption.route)
                                                                const isDisabled = routeOption.is_locked || routeOption.is_selected
                                                                const statusLabel = getRouteStatusLabel(routeOption)
                                                                const pharmacistName = getVisibleSelectedPharmacistName(routeOption)

                                                                return (
                                                                    <button
                                                                        key={routeOption.route}
                                                                        type="button"
                                                                        onClick={() => handleRouteToggle(routeOption)}
                                                                        disabled={isDisabled}
                                                                        className={`group min-h-[80px] rounded-xl border px-2 py-2 text-left transition ${isChecked
                                                                                ? 'border-blue-500 bg-blue-600 text-white shadow-[0_12px_30px_-18px_rgba(37,99,235,0.9)]'
                                                                                : routeOption.is_selected
                                                                                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                                                                                    : routeOption.is_locked
                                                                                        ? 'border-slate-200 bg-slate-100 text-slate-400'
                                                                                        : 'border-slate-200 bg-white text-slate-900 hover:border-blue-200 hover:bg-blue-50'
                                                                            }`}
                                                                    >
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-xl font-semibold tracking-tight">{routeOption.route}</span>
                                                                        </div>
                                                                        <p className={`text-[10px] font-medium ${isChecked ? 'text-blue-100' : 'text-inherit opacity-80'}`}>
                                                                            {statusLabel}
                                                                        </p>
                                                                        {pharmacistName ? (
                                                                            <p className={`text-[10px] ${isChecked ? 'text-blue-100/90' : 'text-slate-400'}`}>
                                                                                {pharmacistName}
                                                                            </p>
                                                                        ) : null}
                                                                    </button>
                                                                )
                                                            })}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center">
                                                        <p className="text-sm font-medium text-slate-600">No routes available.</p>
                                                    </div>
                                                )}

                                                {routeError ? <p className="mt-3 text-sm text-red-600">{routeError}</p> : null}
                                            </div>

                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={handleSkipToDashboard}
                                            disabled={isSubmitting}
                                            className="flex h-14 w-full items-center justify-center rounded-2xl border border-slate-300 bg-white text-lg font-semibold text-slate-700 transition hover:bg-slate-50 focus:ring-4 focus:ring-slate-200 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            Skip
                                        </button>

										<button
											type="button"
											onClick={handleSavePharmacist}
											className={`flex h-14 w-full items-center justify-center rounded-2xl bg-slate-900 text-lg font-semibold text-white shadow-[0_18px_45px_-22px_rgba(15,23,42,0.7)] transition hover:bg-slate-800 focus:ring-4 focus:ring-slate-200 ${(!hidePharmacistSelection && activePharmacistOptions.length === 0) || !selectedPharmacist || selectedRoutes.length === 0 ? 'pointer-events-none opacity-50' : ''}`}>
											{isSubmitting && !showRouteSignatureModal ? <Loader /> : `Continue${selectedRouteCount ? ` (${selectedRouteCount})` : ''}`}
										</button>
                                    </div>
                                </div>
                            </div>
                        </div>
            </AnimatedBottomSheet>
            <HandoverSignatureModal
                isOpen={showRouteSignatureModal}
                title=""
                description="Sign below to continue with these pickup routes."
                confirmLabel="Continue"
                confirmButtonClassName="bg-slate-900 hover:bg-slate-800"
                onCancel={() => setShowRouteSignatureModal(false)}
                onConfirm={handleConfirmDeliverySetupSignature}
                loading={isSubmitting}
            />
        </React.Fragment>
    )
}
